'use client';

/**
 * Catches render errors and shows what actually broke.
 *
 * Next's production build replaces a thrown error with "a client-side
 * exception has occurred", which is untraceable without a source-mapped stack
 * in the console. That is a bad enough debugging experience that it is worth
 * a boundary of our own: the message, the component stack, and a reset that
 * clears the persisted diagram (the usual culprit is a stored payload from an
 * older build that no longer matches what the components expect).
 */

import React from 'react';

interface State {
  error: Error | null;
  info: string | null;
}

export default class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the full stack in the console for source-mapped debugging.
    console.error('Archlyy render error', error, info.componentStack);
    this.setState({ info: info.componentStack ?? null });
  }

  private reset = (clearStorage: boolean) => {
    if (clearStorage) {
      try {
        window.sessionStorage.removeItem('archlyy.diagram');
      } catch {
        /* nothing we can do, reload anyway */
      }
    }
    window.location.reload();
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ background: 'var(--background)' }}
      >
        <div className="card-dark rounded-xl max-w-2xl w-full p-6">
          <h1 className="text-lg font-semibold mb-1">Something broke on this screen</h1>
          <p className="text-sm text-muted-foreground mb-4">
            The rest of the app is fine. Details below — please include them if you report this.
          </p>

          <div className="code-bg rounded-lg p-3 mb-4 overflow-auto max-h-72">
            <p className="text-xs font-mono text-red-400 mb-2 break-words">
              {error.name}: {error.message}
            </p>
            {info && (
              <pre className="text-2xs font-mono text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {info.trim()}
              </pre>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => this.reset(false)} className="btn-secondary text-xs">
              Reload
            </button>
            <button onClick={() => this.reset(true)} className="btn-primary text-xs">
              Clear the open diagram and reload
            </button>
            <a href="/legacy" className="btn-ghost text-xs">
              Open the previous UI
            </a>
          </div>
        </div>
      </div>
    );
  }
}
